import { useContext, } from 'react';
import { UserContext } from '../UserContext';
import React, { useState, useRef, useMemo } from 'react';
import api from '../api';
import { useEffect } from 'react';
import Cropper from 'react-easy-crop'
import getCroppedImg from '../components/getCroppedImg';
import imageCompression from "browser-image-compression";
import { useTranslation } from '../LanguageContext';
import { countries as countryList } from '../utils/countries';
import Modal from '../components/Modal';
import ImageCropModal from '../components/ImageCropModal';
import ProfileFacets from '../components/ProfileFacets';
import MediaGallery, { MediaTypeBadge, isPlayable } from '../components/MediaGallery';
import { uploadImage } from '../utils/cloudflare';
import { Link } from 'react-router-dom';
import { PlatformIcon, PLATFORMS, SOCIAL_PLATFORM_KEYS, detectPlatform, normalizePlatform, hostOf, platformLabel }
    from '../utils/platformIcons';
import { publicProfileUrl, publicProfileLabel } from '../utils/siteUrl';
import ThemeStudio from '../components/ThemeStudio';
import { ProfileBackdrop, resolveTheme } from '../utils/profileTheme';

/**
 * One row shape for every connected thing on the profile — a verified social
 * account, one still in review, or a plain link.
 *
 * They used to be three different pills with three different gradients, three
 * icon treatments and three heights, which is most of why the page read as
 * cluttered. Now the only differences are the ones that carry meaning: the
 * accent stripe on the left, and the badge on the right.
 */
function ProfileRow({ platform, url, title, subtitle, badge, accent, onDelete, deleteLabel, muted, leading }) {
    const rowRef = useRef(null);

    // Only a click that really happened *on the row* opens the link. Anything
    // this row renders into a portal — the thumbnail cropper — is a React
    // descendant but not a DOM one, so its clicks arrive here having never
    // touched the card. Without the check, moving the cropper's zoom slider
    // opened the link in a new tab.
    const open = (event) => {
        if (!url) return;
        if (!rowRef.current?.contains(event.target)) return;
        window.open(url, '_blank', 'noopener,noreferrer');
    };

    return (
        <div
            ref={rowRef}
            onClick={url ? open : undefined}
            className={`group relative flex items-center gap-3 rounded-2xl border bg-white p-2.5 transition
                ${url ? 'cursor-pointer hover:shadow-md hover:-translate-y-px' : ''}
                ${muted ? 'border-slate-200 opacity-90' : 'border-slate-200'}`}
        >
            {/* Accent stripe instead of a full gradient border: it says which
                kind of row this is without competing with the brand colour of
                the icon right next to it. */}
            <span className={`absolute left-0 top-3 bottom-3 w-1 rounded-full ${accent}`} />

            {/* `leading` is how a plain link swaps the derived platform icon
                for its own uploaded thumbnail — and for the control that sets
                one. Connected socials pass nothing and keep the icon. */}
            {leading || <PlatformIcon platform={platform} url={url} size={42} className={`ml-1.5 ${muted ? 'grayscale' : ''}`} />}

            <div className="min-w-0 flex-1">
                <p className="text-sm font-bold text-slate-800 truncate">{title}</p>
                {subtitle && <p className="text-[11px] text-slate-400 truncate">{subtitle}</p>}
            </div>

            {badge}

            {onDelete && (
                <button
                    type="button"
                    aria-label={deleteLabel}
                    onClick={(e) => { e.stopPropagation(); onDelete(); }}
                    className="w-8 h-8 flex items-center justify-center rounded-full text-slate-300 hover:text-red-600 hover:bg-red-50 transition flex-shrink-0"
                >
                    <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                </button>
            )}
        </div>
    );
}

/**
 * A link pointing at a platform the profile can actually *connect* is a missed
 * connection rather than a link: only a connected account carries a verified
 * follower count and reaches search and brand discovery, so someone who pasted
 * their Instagram under "Add Link" is invisible exactly where it matters.
 *
 * Returns the platform key worth warning about, or `null`. Anything outside
 * `SOCIAL_PLATFORM_KEYS` — a shop, a blog — is genuinely just a link and gets
 * no notice, and neither does a platform this account has already connected:
 * a second Instagram link next to a connected Instagram is a choice, not a
 * mistake.
 */
/**
 * The little picture at the head of a link row, and the control that sets it.
 *
 * Deliberately an *upgrade* rather than a requirement: with no upload the row
 * still shows the icon `detectPlatform` derives from the host, and an unknown
 * host still gets its monogram tile. So a link is never worse for not having
 * one, and a shop link, a track or a product can have its own cover.
 *
 * Square, and cropped before upload, because the row frames it `object-cover`
 * at 42px — an uncropped photo publishes as whatever band of itself happens to
 * fit, and the person who uploaded it finds out from the finished profile.
 */
function LinkThumb({ item, onUpdated }) {
    const { t } = useTranslation();
    const inputRef = useRef(null);
    const [file, setFile] = useState(null);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState(null);

    /* Yükleme ve kaydetme iki ayrı iş, iki ayrı hata. Eskiden ikisi de
       "sayfayı yenileyin" diyordu — yenilemek bu hataların hiçbirini
       çözmüyor, üstelik seçilen fotoğrafı da atıyordu. Artık sebep yazıyor,
       kırpma penceresi açık kalıyor ve aynı kare tekrar denenebiliyor. */
    const reason = (err) => {
        const code = err?.response?.status;
        if (code) return ` (${code})`;
        return err?.message ? ` (${err.message})` : '';
    };

    const write = async (icon) => {
        setBusy(true);
        setError(null);
        try {
            const res = await api.put('api/linkview/', { linkId: item.id, icon });
            onUpdated(res.data);
            setFile(null);
        } catch (err) {
            console.error('link thumbnail save failed', err);
            const message = t('profile.thumbSaveError') + reason(err);
            // Kırpma penceresi açıkken hata onun içinde; küçük görseli
            // kaldırırken açık pencere yok, orada tek kanal alert.
            if (file) setError(message); else alert(message);
        } finally {
            setBusy(false);
        }
    };

    const upload = async (cropped) => {
        setBusy(true);
        setError(null);
        try {
            const url = await uploadImage(cropped);
            if (!url) throw new Error('no_upload_url');
            await write(url);   // busy/file durumunu kendisi kapatıyor
        } catch (err) {
            console.error('link thumbnail upload failed', err);
            setError(t('profile.thumbUploadError') + reason(err));
            setBusy(false);
        }
    };

    return (<>
        {/* Its own click target inside a row that opens the link — without the
            stop, setting a thumbnail would also navigate away from the page. */}
        <div
            className="relative ml-1.5 flex-shrink-0 group/thumb"
            onClick={(e) => { e.stopPropagation(); }}
        >
            {item.icon ? (
                <img src={item.icon} alt="" className="w-[42px] h-[42px] rounded-xl object-cover" />
            ) : (
                <PlatformIcon url={item.link} size={42} />
            )}

            <button
                type="button"
                onClick={() => inputRef.current?.click()}
                disabled={busy}
                title={item.icon ? t('profile.thumbReplace') : t('profile.thumbAdd')}
                aria-label={item.icon ? t('profile.thumbReplace') : t('profile.thumbAdd')}
                className="absolute inset-0 rounded-xl bg-slate-900/60 text-white flex items-center justify-center
                    opacity-0 group-hover/thumb:opacity-100 focus:opacity-100 transition"
            >
                {busy ? (
                    <span className="spinner-sm" />
                ) : (
                    <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                        <path strokeLinecap="round" strokeLinejoin="round" d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                )}
            </button>

            {item.icon && !busy && (
                <button
                    type="button"
                    onClick={() => write(null)}
                    title={t('profile.thumbRemove')}
                    aria-label={t('profile.thumbRemove')}
                    className="absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full bg-white border border-slate-200 text-slate-400
                        flex items-center justify-center shadow-sm opacity-0 group-hover/thumb:opacity-100 transition hover:text-red-600"
                >
                    <svg xmlns="http://www.w3.org/2000/svg" className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                    </svg>
                </button>
            )}

            <input
                ref={inputRef}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={(e) => {
                    const picked = e.target.files?.[0];
                    if (picked) { setError(null); setFile(picked); }
                    e.target.value = '';   // same file twice has to re-fire
                }}
            />
        </div>

        {file && (
            <ImageCropModal
                file={file}
                aspect={1}
                title={t('profile.thumbCropTitle')}
                hint={t('profile.thumbCropHint')}
                busy={busy}
                error={error}
                onCancel={() => { setFile(null); setError(null); }}
                onCropped={upload}
            />
        )}
    </>);
}

function missedSocial(url, user) {
    const key = detectPlatform(url);
    if (!key || !SOCIAL_PLATFORM_KEYS.includes(key)) return null;
    const connected = [...(user?.socials || []), ...(user?.manule_socials || [])]
        .some((item) => normalizePlatform(item.social_media) === key);
    return connected ? null : key;
}

function Profile() {
    const { t } = useTranslation();
    const { user, setUser } = useContext(UserContext);
    const [copied, setCopied] = useState(false);
    /* Owned here rather than inside AddSocialMedia so the warning on a social
       link can hand the user straight to the right form. */
    const [socialFormOpen, setSocialFormOpen] = useState(false);
    const [themeOpen, setThemeOpen] = useState(false);

    const theme = useMemo(() => resolveTheme(user?.theme), [user?.theme]);

    const copyProfileLink = () => {
        const link = publicProfileUrl(user.username);
        navigator.clipboard.writeText(link).then(() => {
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        });
    };

    const deleteLink = async (linkId) => {
        try {
            const res = await api.delete(`api/linkview/`, {
                data: { linkId }
            });
            if (res.status === 200) {
                setUser(prevUser => ({
                    ...prevUser,
                    links: prevUser.links.filter(link => link.id !== linkId)
                }));
            }
        }
        catch (error) {
            alert(t('profile.errorMsg'));
        }
    }

    /* Replaces one row in place — a thumbnail change must not reorder the list
       or drop the rows around it. */
    const updateLink = (saved) => {
        setUser(prevUser => ({
            ...prevUser,
            links: prevUser.links.map(link => (link.id === saved.id ? saved : link)),
        }));
    };

    const handleVerificationRequest = async () => {
        try {
            const res = await api.post('api/verification-request/', {});
            if (res.status === 200 || res.status === 201) {
                setUser(prevUser => ({
                    ...prevUser,
                    verification_request: "pending"
                }));
            }
        } catch (error) {
            alert(t('profile.errorMsg'));
        }
    }

    const deleteSocial = async (socialId) => {
        try {
            const res = await api.delete(`api/social_media/`, {
                data: { socialId }
            });
            if (res.status === 200) {
                setUser(prevUser => ({
                    ...prevUser,
                    socials: prevUser.socials.filter(social => social.id !== socialId)
                }));
            }
        }
        catch (error) {
            alert(t('profile.errorMsg'));
        }
    }

    const deleteManuelSocial = async (socialId) => {
        try {
            const res = await api.delete(`api/manuel_social_media/`, {
                data: { socialId }
            });
            if (res.status === 200) {
                setUser(prevUser => ({
                    ...prevUser,
                    manule_socials: prevUser.manule_socials.filter(social => social.id !== socialId)
                }));
            }
        }
        catch (error) {
            alert(t('profile.errorMsg'));
        }
    }

    const deletePP = async () => {
        try {
            const res = await api.delete(`api/save_pp/`);

            if (res.status === 200) {
                // Varsayılan avatarın adresini sunucu söylüyor; burada tekrar
                // kurmak, iki tarafın farklı URL'de anlaşmasına yol açıyordu.
                setUser(prevUser => ({
                    ...prevUser,
                    profile_picture: res.data
                }));
            }
        }
        catch (error) {
        }
    }

    function AddLink() {
        const { user, setUser } = useContext(UserContext);
        const [errors, setErrors] = useState([]);
        const [show, setShow] = useState(false);
        const [loading, setLoading] = useState(false)
        const [link, setLink] = useState("");
        const [text, setText] = useState("");

        const handleShow = () => { setShow(true); };
        const handleClose = () => { setLink(""); setText(""); setErrors([]); setShow(false); }

        const handleSubmit = async (e) => {
            e.preventDefault();
            try {
                setLoading(true);
                const res = await api.post('api/linkview/', { link, text });
                if (res.status === 200) {
                    setUser(prevUser => ({
                        ...prevUser,
                        links: [...prevUser.links, res.data]
                    }));
                }
                handleClose();
            }
            catch (error) {
                setErrors([error.response.data])
                return;
            }
            finally {
                setLoading(false)
            }
        }

        // What the link will look like once saved, while it is still being
        // typed. Detection is the same function the row itself uses, so the
        // preview cannot promise an icon the list will not show.
        const detected = detectPlatform(link);
        const host = hostOf(link);
        const missed = missedSocial(link, user);

        return (<>
            <div className="mt-3">
                <button type="button" onClick={handleShow}
                    className="flex items-center gap-2 px-5 py-2.5 bg-primary-600 text-white font-medium rounded-full hover:bg-primary-700 transition duration-200 shadow-md"
                >
                    <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
                    </svg>
                    {t('profile.addLinkBtn')}
                </button>
            </div>

            <Modal
                open={show}
                onClose={handleClose}
                title={t('profile.addLinkTitle')}
                subtitle={t('profile.addLinkHint')}
                footer={
                    <button className="btn-primary w-full flex items-center justify-center" type="submit"
                        form="add-link-form" disabled={loading}>
                        {loading ? <div className="spinner-sm" /> : t('profile.submitBtn')}
                    </button>
                }
            >
                <form id="add-link-form" onSubmit={handleSubmit} className="space-y-4">
                    <input className="input" type="text" value={link}
                        onChange={(e) => setLink(e.target.value)}
                        placeholder={t('profile.linkUrlPlaceholder')} maxLength={254} required />

                    {host && (
                        <div className="flex items-center gap-3 rounded-2xl border border-slate-200 bg-slate-50 p-2.5">
                            <PlatformIcon url={link} size={40} />
                            <div className="min-w-0 flex-1">
                                <p className="text-sm font-bold text-slate-800 truncate">
                                    {text || (detected ? PLATFORMS[detected].label : host)}
                                </p>
                                <p className="text-[11px] text-slate-400 truncate">{host}</p>
                            </div>
                            {detected ? (
                                <span className="text-[10px] font-bold text-emerald-700 bg-emerald-50 border border-emerald-200 px-2 py-0.5 rounded-full flex-shrink-0">
                                    {t('profile.linkDetected')}
                                </span>
                            ) : (
                                <span className="text-[10px] font-bold text-slate-500 bg-white border border-slate-200 px-2 py-0.5 rounded-full flex-shrink-0">
                                    {t('profile.linkGeneric')}
                                </span>
                            )}
                        </div>
                    )}

                    {/* Shown while the URL is still being typed, not after the
                        link is saved — a warning that arrives once the mistake
                        is made is a chore rather than a help. It does not block
                        the save: someone may want the link as well. */}
                    {missed && (
                        <div className="rounded-2xl border border-amber-200 bg-amber-50 p-3">
                            <div className="flex items-start gap-2">
                                <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5 text-amber-500 flex-shrink-0 mt-px" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
                                </svg>
                                <div className="min-w-0">
                                    <p className="text-xs font-bold text-amber-900">
                                        {t('profile.linkIsSocialTitle').replace('{platform}', PLATFORMS[missed].label)}
                                    </p>
                                    <p className="text-[11px] text-amber-800 leading-relaxed mt-0.5">
                                        {t('profile.linkIsSocialBody')}
                                    </p>
                                </div>
                            </div>
                            <button type="button"
                                onClick={() => { handleClose(); setSocialFormOpen(true); }}
                                className="mt-2 w-full rounded-xl bg-white border border-amber-300 px-3 py-2 text-xs font-bold text-amber-900 hover:bg-amber-100 transition"
                            >
                                {t('profile.linkIsSocialAction')}
                            </button>
                        </div>
                    )}

                    <div>
                        <input className="input" type="text" value={text}
                            onChange={(e) => setText(e.target.value)}
                            placeholder={t('profile.titlePlaceholder')} maxLength={254} required />
                        {/* Naming the detected platform is a one-tap answer to
                            a field most people would otherwise leave vague. */}
                        {detected && text !== PLATFORMS[detected].label && (
                            <button type="button" onClick={() => setText(PLATFORMS[detected].label)}
                                className="text-[11px] font-semibold text-primary-700 hover:underline mt-1.5">
                                {t('profile.useDetectedTitle')} "{PLATFORMS[detected].label}"
                            </button>
                        )}
                    </div>

                    {errors.length > 0 && (
                        <ul className="list-none p-0 space-y-1">
                            {errors.map((error, index) => (
                                <li key={index} className="text-sm text-red-600">{` ${error}`}</li>
                            ))}
                        </ul>
                    )}
                </form>
            </Modal>
        </>)
    }

    function AddSocialMedia() {
        const { user, setUser } = useContext(UserContext);
        const [socialMediaUsername, setSocialMediaUsername] = useState("");
        const [socialMedia, setSocialMedia] = useState("");
        const [errors, setErrors] = useState([]);
        const [loading, setLoading] = useState(false);
        const [isCopied, setIsCopied] = useState(false);
        const textRef = useRef(null);

        const handleCopy = () => {
            if (textRef.current) {
                navigator.clipboard.writeText(textRef.current.innerText).then(() => {
                    setIsCopied(true);
                    setTimeout(() => setIsCopied(false), 2000);
                });
            }
        };

        /* Open state lives on the page, so the "this is a social account"
           warning in AddLink can open this form directly. */
        const show = socialFormOpen;
        const handleShow = () => setSocialFormOpen(true);
        const handleClose = () => {
            setErrors([]);
            setSocialMedia("");
            setSocialMediaUsername("");
            setSocialFormOpen(false);
        }

        const handleManualSubmit = async (e) => {
            e.preventDefault();
            if (socialMedia === "") {
                setErrors([t('profile.platformError')]);
                return;
            }
            if (socialMediaUsername === "") {
                setErrors([t('profile.usernameError')]);
                return;
            }

            try {
                setLoading(true);
                const res = await api.post('api/manuel_social_media/', { socialMedia, socialMediaUsername, tags: [], countrys: [] });
                if (res.status === 200 && !(user.manule_socials?.some(ms => ms.social_media === socialMedia))) {
                    setUser(prevUser => ({
                        ...prevUser,
                        manule_socials: [...(prevUser.manule_socials || []), res.data]
                    }));
                }
                handleClose();
            } catch (error) {
                setErrors([error.response?.data || "Error"]);
                return;
            } finally {
                setLoading(false);
            }
        }

        return (<>
            <div className="mt-3">
                <button type="button" onClick={handleShow}
                    className="flex items-center gap-2 px-5 py-2.5 bg-white text-slate-700 font-medium rounded-full hover:bg-slate-50 transition border border-slate-200 shadow-sm"
                >
                    <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5 text-primary-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
                    </svg>
                    {t('profile.addSocialMediaBtn')}
                </button>
            </div>

            <Modal
                open={show}
                onClose={handleClose}
                title={t('profile.addSocialMediaTitle')}
                subtitle={t('profile.addSocialMediaHint')}
                footer={
                    <button className="btn-primary w-full flex items-center justify-center" type="submit"
                        form="add-social-form" disabled={loading || !socialMedia}>
                        {loading ? <div className="spinner-sm" /> : t('profile.submitBtn')}
                    </button>
                }
            >
                <form id="add-social-form" onSubmit={handleManualSubmit} className="space-y-4">
                    {/* A grid of marks rather than a dropdown: with 26 options
                        a <select> is a scrolling list of words, and the whole
                        point of a platform is that it is recognisable on
                        sight. */}
                    <div>
                        <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2 block">
                            {t('profile.platformSelect')}
                        </label>
                        <div className="grid grid-cols-4 sm:grid-cols-5 gap-2">
                            {SOCIAL_PLATFORM_KEYS.map((key) => {
                                const meta = PLATFORMS[key];
                                const active = socialMedia === meta.label;
                                return (
                                    <button key={key} type="button"
                                        onClick={() => setSocialMedia(meta.label)}
                                        title={meta.label}
                                        className={`flex flex-col items-center gap-1 rounded-xl border-2 px-1 py-2 transition ${active
                                            ? 'border-primary-500 bg-primary-50'
                                            : 'border-transparent bg-slate-50 hover:bg-slate-100'}`}
                                    >
                                        <PlatformIcon platform={key} size={30} />
                                        <span className={`text-[9px] font-semibold leading-tight text-center truncate w-full ${active ? 'text-primary-700' : 'text-slate-500'}`}>
                                            {meta.label}
                                        </span>
                                    </button>
                                );
                            })}
                        </div>
                    </div>

                    <input className="input" type="text" value={socialMediaUsername}
                        onChange={(e) => setSocialMediaUsername(e.target.value)}
                        placeholder={t('profile.socialMediaUsernamePlaceholder')} maxLength={254} required />

                    {socialMedia && socialMediaUsername && (
                        <div className="flex items-center gap-3 rounded-2xl border border-slate-200 bg-slate-50 p-2.5">
                            <PlatformIcon platform={socialMedia} size={40} />
                            <div className="min-w-0 flex-1">
                                <p className="text-sm font-bold text-slate-800 truncate">{socialMedia}</p>
                                <p className="text-[11px] text-slate-400 truncate">@{socialMediaUsername.replace(/^@/, '')}</p>
                            </div>
                        </div>
                    )}

                    {errors.length > 0 && (
                        <ul className="list-none p-0 space-y-1">
                            {errors.map((error, index) => (
                                <li key={index} className="text-sm text-red-600">{` ${error}`}</li>
                            ))}
                        </ul>
                    )}

                    <p className="text-xs text-slate-500 text-center leading-relaxed">
                        {t('profile.approvalWarning')}
                    </p>

                    <div className="p-4 bg-amber-50 border border-amber-100 rounded-xl">
                        <div className="flex items-center justify-between bg-white px-3 py-2 rounded-lg border border-amber-200 mb-2">
                            <span ref={textRef} className="text-sm font-medium text-slate-700 truncate">{publicProfileLabel(user.username)}</span>
                            <button onClick={handleCopy} type="button" className="flex items-center gap-1 px-3 py-1 bg-primary-50 text-primary-600 hover:bg-primary-100 rounded-md text-xs font-semibold transition ml-2 shrink-0">
                                {isCopied ? (
                                    <svg xmlns="http://www.w3.org/2000/svg" className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                        <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                                    </svg>
                                ) : (
                                    <svg xmlns="http://www.w3.org/2000/svg" className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                        <path strokeLinecap="round" strokeLinejoin="round" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                                    </svg>
                                )}
                                {isCopied ? t('profile.copiedBtn') : t('profile.copyBtn')}
                            </button>
                        </div>
                        <p className="text-xs text-amber-700 leading-relaxed">
                            {t('profile.linkCopyWarning')}
                        </p>
                    </div>
                </form>
            </Modal>
        </>)
    }

    function ChangePP() {
        const [imageSrc, setImageSrc] = useState(null);
        const [crop, setCrop] = useState({ x: 0, y: 0 });
        const [zoom, setZoom] = useState(1);
        const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);

        const handleClose = () => { setImageSrc(null); }

        const onCropComplete = (croppedArea, croppedAreaPixels) => {
            setCroppedAreaPixels(croppedAreaPixels);
        };

        const handleFileChange = async (e) => {
            const file = e.target.files[0];
            if (!file) return;
            const options = { maxSizeMB: 1, maxWidthOrHeight: 1280, useWebWorker: true };
            try {
                const compressedFile = await imageCompression(file, options);
                const imageDataUrl = await readFile(compressedFile);
                setImageSrc(imageDataUrl);
            } catch (error) {
            }
        };

        // Kırpılan kare önce Cloudflare Images'a gider, sunucuya yalnızca
        // teslim URL'si yazılır — galeri ve medya kiti zaten böyle çalışıyor.
        // Cloudflare el sıkışması başarısız olursa eski base64 yolu devrede
        // kalıyor, yoksa CDN'deki bir aksaklık fotoğraf değiştirmeyi tümden
        // bloklardı.
        const handleCrop = async () => {
            try {
                const croppedImage = await getCroppedImg(imageSrc, croppedAreaPixels);
                const response = await fetch(croppedImage);
                const blob = await response.blob();

                const type = blob.type || 'image/jpeg';
                const file = new File([blob], `pp.${type.split('/')[1] || 'jpg'}`, { type });

                let url = null;
                try { url = await uploadImage(file); } catch (e) { url = null; }

                if (url) {
                    const res = await api.post('api/save_pp/', { pp: url });
                    if (res.status === 200) {
                        setUser(prevUser => ({ ...prevUser, profile_picture: url }));
                        handleClose();
                    }
                    return;
                }

                const reader = new FileReader();
                reader.readAsDataURL(blob);
                reader.onloadend = async () => {
                    const base64data = reader.result;
                    const res = await api.post('api/save_pp/', { pp: base64data });
                    if (res.status === 200) {
                        setUser(prevUser => ({ ...prevUser, profile_picture: res.data }));
                        handleClose();
                    }
                }
            } catch (e) { return; }
        };

        const readFile = (file) => {
            return new Promise((resolve) => {
                const reader = new FileReader();
                reader.addEventListener('load', () => resolve(reader.result));
                reader.readAsDataURL(file);
            });
        };

        return (<>
            <label className="inline-flex items-center gap-2 px-4 py-2 bg-primary-600 text-white text-sm font-medium rounded-xl hover:bg-primary-700 cursor-pointer transition duration-200">
                {t('profile.changePhotoBtn')}
                <input type="file" accept="image/*" onChange={handleFileChange} className="hidden" />
            </label>

            {/* Opens on top of the edit-profile dialog. Nesting is why the
                backdrop used to go almost black — Modal only lets the
                outermost layer paint a scrim. `closeOnBackdrop` is off here
                because dragging the crop handle regularly ends outside the
                panel, and losing the photo to that is infuriating. */}
            <Modal
                open={Boolean(imageSrc)}
                onClose={handleClose}
                title={t('profile.changePhotoBtn')}
                closeOnBackdrop={false}
                footer={
                    <div className="flex gap-3">
                        <button type="button" className="btn-secondary flex-1 py-2.5 text-sm" onClick={handleClose}>
                            {t('profile.closeBtn')}
                        </button>
                        <button type="button" className="btn-primary flex-1 py-2.5 text-sm" onClick={handleCrop}>
                            {t('profile.saveBtn')}
                        </button>
                    </div>
                }
            >
                <div className="relative w-full overflow-hidden rounded-2xl bg-slate-900" style={{ height: 320 }}>
                    <Cropper image={imageSrc} crop={crop} zoom={zoom} aspect={3 / 3} cropShape="round" showGrid={false}
                        onCropChange={setCrop} onCropComplete={onCropComplete} onZoomChange={setZoom} />
                </div>
                <div className="mt-4">
                    <label className="label">{t('profile.zoomLabel')}</label>
                    <input id="zoomRange" type="range" min={1} max={3} step={0.1} value={zoom}
                        onChange={(e) => setZoom(parseFloat(e.target.value))} className="w-full accent-primary-600" />
                </div>
            </Modal>
        </>);
    }

    function EditProfile() {
        const { user, setUser } = useContext(UserContext);
        const [bio, setBio] = useState(user.profile_text);
        const [errors, setErrors] = useState([]);
        const [show, setShow] = useState(false);
        const [loading, setLoading] = useState(false);
        const [tags, setTags] = useState(user.profile_tags || []);
        const [tag, setTag] = useState("");
        const [countrys, setCountrys] = useState(user.profile_countrys || []);
        // eslint-disable-next-line
        const [tagsuggeslist, setSagsuggeslist] = useState([]);
        // eslint-disable-next-line
        const [checked, setChecked] = useState(user.is_content_producer);

        useEffect(() => {
            if (tag === "") { setSagsuggeslist([]); return; }
            setSagsuggeslist(user.tags.filter((t) => t.name.toLowerCase().startsWith(tag.toLowerCase())));
            // eslint-disable-next-line
        }, [tag]);

        const handleAddTag = (tagName) => {
            if (!tagName) return;
            if (tags.length >= 10) { setErrors(["You can only select 10 tags."]); return; }
            if (!tags.includes(tagName)) { setTags([...tags, tagName]); }
            setTag("");
        };

        const handleCountryChange = (event) => {
            const selected = event.target.value;
            if (countrys.length >= 5) { setErrors(["You can only select 5 countries."]); return; }
            if (selected !== "" && !countrys.includes(selected)) { setCountrys([...countrys, selected]); }
            event.target.value = "";
        };

        const handleShow = () => { setShow(true); };
        const handleSubmit = async (e) => {
            e.preventDefault();
            if (tags.length > 10) { setErrors(["You can only select 10 tags."]); return; }
            try {
                setLoading(true);
                await api.post('api/edit_profile/', { bio, tags, countrys, checked });
                setUser(prevUser => ({ ...prevUser, profile_text: bio, profile_tags: tags, profile_countrys: countrys, is_content_producer: checked }));
                handleClose();
            }
            catch (error) { setErrors([error.response.data]); return; }
            finally { setLoading(false) }
        }

        const handleClose = () => { setErrors([]); setTag(""); setShow(false); }

        return (<>
            <button onClick={handleShow}
                className="px-4 py-2 bg-slate-100 text-slate-700 font-medium rounded-xl hover:bg-slate-200 transition text-sm shadow-sm"
            >
                {t('profile.editProfileBtn')}
            </button>

            <Modal
                open={show}
                onClose={handleClose}
                title={t('profile.editProfileBtn')}
                footer={
                    <button className="btn-primary w-full flex items-center justify-center" type="submit"
                        form="edit-profile-form" disabled={loading}>
                        {loading ? <div className="spinner-sm" /> : t('profile.submitBtn')}
                    </button>
                }
            >
                {/* Photo controls sit at the top of the body rather than in
                    the header bar: the header is a fixed-height row and two
                    buttons wrapping inside it was what made the dialog's top
                    edge look ragged. */}
                <div className="flex items-center gap-3 rounded-2xl border border-slate-200 bg-slate-50 p-3 mb-4">
                    <img className="w-14 h-14 rounded-full object-cover ring-2 ring-white shadow-sm flex-shrink-0"
                        src={user.profile_picture} alt="" />
                    <div className="flex-1 min-w-0 flex flex-wrap gap-2">
                        <ChangePP />
                        <button type="button"
                            className="px-4 py-2 rounded-xl text-sm font-semibold text-red-600 bg-red-50 border border-red-200 hover:bg-red-100 transition"
                            onClick={deletePP}>
                            {t('profile.deletePhotoBtn')}
                        </button>
                    </div>
                </div>

                        <form id="edit-profile-form" onSubmit={handleSubmit} className="space-y-4">
                            <div>
                                <h4 className="text-sm font-semibold text-slate-700 mb-2">{t('profile.bioTextLabel')}</h4>
                                <textarea className="input min-h-[100px] resize-y" type="text" value={bio} onChange={(e) => setBio(e.target.value)} placeholder={t('profile.bioPlaceholder')} maxLength={500} />
                                <p className="text-xs text-slate-400 mt-1">{t('profile.bioHelp')}</p>
                            </div>

                            {/* Profile Tags */}
                            <div>
                                <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5 block">{t('profile.tagsLabel') || 'Profile Tags'}</label>
                                <div className="flex gap-2 mb-2 relative">
                                    <input
                                        type="text"
                                        className="input py-2 text-sm flex-1"
                                        placeholder={t('profile.tagPlaceholder') || 'Add a tag...'}
                                        value={tag}
                                        onChange={(e) => setTag(e.target.value)}
                                        onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddTag(tag))}
                                    />
                                    <button type="button" onClick={() => handleAddTag(tag)} className="btn-secondary py-2 px-4 text-sm">{t('profile.addTagBtn') || 'Add'}</button>
                                    {tagsuggeslist.length > 0 && (
                                        <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-slate-200 rounded-xl shadow-lg z-30 max-h-48 overflow-y-auto">
                                            {tagsuggeslist.map((t_opt, idx) => (
                                                <button key={idx} type="button" onClick={() => handleAddTag(t_opt.name)} className="w-full text-left px-4 py-2 text-sm text-slate-700 hover:bg-slate-50 transition border-b border-slate-100 last:border-0">
                                                    {t_opt.name}
                                                </button>
                                            ))}
                                        </div>
                                    )}
                                </div>
                                {tags.length > 0 && (
                                    <div className="flex flex-wrap gap-2">
                                        {tags.map((tg, idx) => (
                                            <span key={idx} onClick={() => setTags(tags.filter((_, i) => i !== idx))} className="badge bg-primary-50 text-primary-600 hover:bg-red-50 hover:text-red-500 cursor-pointer flex items-center gap-1 transition">
                                                🏷️ {tg} <span className="font-bold ml-1">×</span>
                                            </span>
                                        ))}
                                    </div>
                                )}
                            </div>

                            {/* Community Locations */}
                            <div>
                                <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5 block">{t('profile.locationsLabel') || 'Community Locations'}</label>
                                <select className="input py-2 text-sm mb-2" onChange={handleCountryChange} defaultValue="">
                                    <option value="" disabled>{t('profile.locationPlaceholder') || 'Select a location...'}</option>
                                    {countryList.map((country, idx) => (
                                        <option key={idx} value={country}>{country}</option>
                                    ))}
                                </select>
                                {countrys.length > 0 && (
                                    <div className="flex flex-wrap gap-2">
                                        {countrys.map((c, idx) => (
                                            <span key={idx} onClick={() => setCountrys(countrys.filter((_, i) => i !== idx))} className="badge bg-slate-100 text-slate-600 hover:bg-red-50 hover:text-red-500 cursor-pointer flex items-center gap-1 transition">
                                                🌍 {c} <span className="font-bold ml-1">×</span>
                                            </span>
                                        ))}
                                    </div>
                                )}
                            </div>

                            {/* <div>
                                <label className="flex items-center gap-3 cursor-pointer">
                                    <div className="relative inline-flex items-center cursor-pointer">
                                        <input 
                                            type="checkbox" 
                                            className="sr-only peer" 
                                            checked={checked} 
                                            onChange={(e) => setChecked(e.target.checked)} 
                                        />
                                        <div className="w-11 h-6 bg-slate-200 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-primary-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-600"></div>
                                    </div>
                                    <span className="text-sm font-semibold text-slate-700">{t('profile.creatorToggleLabel')}</span>
                                </label>
                                <p className="text-xs text-slate-400 mt-1 ml-14">{t('profile.creatorToggleHelp')}</p>
                            </div> */}

                            {errors.length > 0 && (
                                <ul className="list-none p-0 space-y-1">
                                    {errors.map((error, index) => (
                                        <li key={index} className="text-sm text-red-600">{` ${error}`}</li>
                                    ))}
                                </ul>
                            )}
                        </form>
            </Modal>
        </>)
    }

    function ChangeVisibility() {
        const [loading, setLoading] = useState(false);

        const handleVisibility = async () => {
            try {
                setLoading(true);
                const res = await api.post('/api/change_visibility/');
                if (res.status === 200) {
                    setUser(prevUser => ({ ...prevUser, is_active: res.data[0] }));
                }
            }
            catch (error) { }
            finally { setLoading(false); }
        }

        if (loading) {
            return <div className="mt-3"><div className="spinner-sm"></div></div>;
        }
        return (
            <div className="flex flex-col items-center gap-2 mt-3 mb-4">
                {user.is_active ? <>
                    <button onClick={handleVisibility} className="flex items-center gap-2 px-4 py-2 bg-emerald-100 text-emerald-700 rounded-xl hover:bg-emerald-200 transition text-sm font-medium">
                        <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                            <path strokeLinecap="round" strokeLinejoin="round" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                        </svg>
                        {t('profile.visibleBtn')}
                    </button>
                    <p className="text-xs text-slate-500 text-center max-w-[280px]">{t('profile.visibleHelp')}</p>
                </> : <>
                    <button onClick={handleVisibility} className="flex items-center gap-2 px-4 py-2 bg-red-100 text-red-700 rounded-xl hover:bg-red-200 transition text-sm font-medium">
                        <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                        </svg>
                        {t('profile.hiddenBtn')}
                    </button>
                    <p className="text-xs text-slate-500 text-center max-w-[280px]">{t('profile.hiddenHelp')}</p>
                </>}
            </div>
        )
    }

    /** Verified accounts — the platform confirmed the follower count. */
    function RenderSocials() {
        if (!user.socials || user.socials.length === 0) return null;
        return (<>
            {user.socials.map((item) => (
                <ProfileRow
                    key={item.id}
                    platform={item.social_media}
                    url={item.link}
                    title={platformLabel(item.social_media)}
                    subtitle={item.username ? `@${String(item.username).replace(/^@/, '')}` : hostOf(item.link)}
                    accent="bg-primary-500"
                    onDelete={() => deleteSocial(item.id)}
                    deleteLabel={t('profile.deleteBtn')}
                    badge={
                        <span className="hidden xs:flex items-center gap-1 text-[11px] font-bold text-slate-600 bg-slate-100 px-2 py-1 rounded-lg flex-shrink-0 tabular-nums">
                            <svg xmlns="http://www.w3.org/2000/svg" className="w-3.5 h-3.5 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                <path strokeLinecap="round" strokeLinejoin="round" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                            </svg>
                            {Number(item.followers || 0).toLocaleString('en-US')}
                        </span>
                    }
                />
            ))}
        </>);
    }

    /** Submitted but not yet confirmed. Same row, greyed mark and a clock. */
    function RenderManuelSocials() {
        if (!user.manule_socials || user.manule_socials.length === 0) return null;
        return (<>
            {user.manule_socials.map((item) => (
                <ProfileRow
                    key={item.id}
                    platform={item.social_media}
                    title={platformLabel(item.social_media)}
                    subtitle={item.username ? `@${String(item.username).replace(/^@/, '')}` : null}
                    accent="bg-amber-400"
                    muted
                    onDelete={() => deleteManuelSocial(item.id)}
                    deleteLabel={t('profile.deleteBtn')}
                    badge={
                        <span className="flex items-center gap-1 text-[10px] font-bold text-amber-700 bg-amber-50 border border-amber-200 px-2 py-1 rounded-lg flex-shrink-0">
                            <svg xmlns="http://www.w3.org/2000/svg" className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                            {t('profile.inVerification')}
                        </span>
                    }
                />
            ))}
        </>);
    }

    /**
     * Plain links, now carrying the icon of whatever site they point at —
     * `detectPlatform` inside `PlatformIcon` does the work, so a YouTube link
     * looks exactly like a connected YouTube account, and an unknown shop gets
     * a stable monogram rather than a generic chain graphic for everything.
     */
    function RenderLinks() {
        if (!user.links || user.links.length === 0) return null;
        return (<>
            {user.links.map((item) => {
                // The same warning as the add form, for the links already
                // saved that way — otherwise the notice only ever reaches
                // people who have not made the mistake yet.
                const missed = missedSocial(item.link, user);
                return (
                    <ProfileRow
                        key={item.id}
                        url={item.link}
                        title={item.title}
                        subtitle={hostOf(item.link)}
                        accent={missed ? 'bg-amber-400' : 'bg-slate-300'}
                        leading={<LinkThumb item={item} onUpdated={updateLink} />}
                        onDelete={() => deleteLink(item.id)}
                        deleteLabel={t('profile.deleteBtn')}
                        badge={missed && (
                            <button
                                type="button"
                                onClick={(e) => { e.stopPropagation(); setSocialFormOpen(true); }}
                                title={t('profile.linkIsSocialAction')}
                                className="flex items-center gap-1 text-[10px] font-bold text-amber-700 bg-amber-50 border border-amber-200 px-2 py-1 rounded-lg flex-shrink-0 hover:bg-amber-100 transition"
                            >
                                <svg xmlns="http://www.w3.org/2000/svg" className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
                                </svg>
                                {t('profile.linkIsSocialBadge')}
                            </button>
                        )}
                    />
                );
            })}
        </>);
    }

    /**
     * The way into the appearance editor.
     *
     * What used to be here was a colour picker and a crop dialog for a
     * wallpaper — except the crop dialog could never open: its `imageSrc` was
     * declared with no setter and no file input fed it, so the only working
     * control on the whole screen was the colour. `ThemeStudio` replaces both.
     */
    function ChangeBackground() {
        return (
            <button
                onClick={() => setThemeOpen(true)}
                title={t('theme.title')}
                aria-label={t('theme.title')}
                className="absolute top-4 left-4 z-20 w-10 h-10 rounded-full bg-slate-100 flex items-center justify-center text-slate-600 hover:bg-slate-200 transition"
            >
                <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M7 21a4 4 0 01-4-4V5a2 2 0 012-2h4a2 2 0 012 2v12a4 4 0 01-4 4zm0 0h12a2 2 0 002-2v-4a2 2 0 00-2-2h-2.343M11 7.343l1.657-1.657a2 2 0 012.828 0l2.829 2.829a2 2 0 010 2.828l-8.486 8.485M7 17h.01" />
                </svg>
            </button>
        );
    }

    // Filtered rather than counted raw: a row that is neither photo nor video
    // never enters the viewer, so counting it would print a number the grid
    // does not back up.
    const galleryItems = (user.gallery || []).filter(isPlayable);

    return (
        <div className="relative min-h-screen pb-24">
            {/* View button.

                It is the one control that floats over the account's own
                wallpaper rather than sitting on the white card, so it cannot
                borrow the card's contrast — and the wallpaper is arbitrary.
                White-on-white/20 was legible over a dark photo and invisible
                over a light one, which is half the catalogue. An opaque light
                fill with dark text is the only pairing that survives both. */}
            {user.is_active &&
                <button onClick={() => window.open(publicProfileUrl(user.username), "_blank")}
                    className="fixed top-4 right-4 z-30 px-4 py-2 bg-white/90 backdrop-blur-md text-slate-900 font-medium rounded-xl border border-slate-200 hover:bg-white transition text-sm shadow-lg"
                >
                    {t('profile.viewBtn')}
                </button>
            }

            {/* The account's own wallpaper.
                Only the wallpaper: this page is the editor, and it is dense
                with controls that need a predictable contrast to stay usable —
                delete buttons, disabled states, form fields. Applying a
                `neon` or `gold` card here would restyle the machinery along
                with the page. What the theme actually looks like is answered
                twice over: live inside the studio, and for real behind the
                "View" button up in the corner. */}
            <ProfileBackdrop theme={theme} />

            {/* Content */}
            <div className="relative z-10 flex flex-col items-center pt-8 px-4">
                <div className="w-full max-w-md bg-white/95 backdrop-blur-xl rounded-[2.5rem] shadow-2xl p-6 relative mt-4">
                    <ChangeBackground />

                    {/* Copy profile link button */}
                    <button
                        onClick={copyProfileLink}
                        className="absolute top-4 right-4 z-20 w-10 h-10 rounded-full bg-slate-100 flex items-center justify-center text-slate-600 hover:bg-slate-200 transition"
                        title={t('profile.copyLink') || 'Copy profile link'}
                    >
                        {copied ? (
                            <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5 text-emerald-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                            </svg>
                        ) : (
                            <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                <path strokeLinecap="round" strokeLinejoin="round" d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
                            </svg>
                        )}
                    </button>

                    {/* Profile picture */}
                    <div className="flex justify-center mb-4 pt-4">
                        <div className="relative">
                            <img className="w-20 h-20 rounded-full ring-4 ring-white/50 object-cover shadow-lg" src={user.profile_picture} alt="profile" />
                            {(user.is_brand || user.is_content_producer) && (
                                <div
                                    className={`absolute -bottom-1 -right-1 w-6 h-6 rounded-full flex items-center justify-center ring-2 ring-white ${user.verified ? (user.is_brand ? 'bg-yellow-500' : 'bg-blue-500') : 'bg-gray-400'}`}
                                    title={user.verified ? (user.is_brand ? t('profile.verifiedBrand') : t('profile.verifiedCreator')) : (user.is_brand ? t('profile.unverifiedBrand') : t('profile.unverifiedCreator'))}
                                >
                                    {user.is_brand ? (
                                        <svg xmlns="http://www.w3.org/2000/svg" className="w-3.5 h-3.5 text-white" fill="currentColor" viewBox="0 0 20 20">
                                            <path fillRule="evenodd" d="M2.166 4.999A11.954 11.954 0 0010 1.944 11.954 11.954 0 0017.834 5c.11.65.166 1.32.166 2.001 0 5.225-3.34 9.67-8 11.317C5.34 16.67 2 12.225 2 7c0-.682.057-1.35.166-2.001zm11.541 3.708a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                                        </svg>
                                    ) : (
                                        <svg xmlns="http://www.w3.org/2000/svg" className="w-3.5 h-3.5 text-white" fill="currentColor" viewBox="0 0 16 16">
                                            <path d="M10.067.87a2.89 2.89 0 0 0-4.134 0l-.622.638-.89-.011a2.89 2.89 0 0 0-2.924 2.924l.01.89-.636.622a2.89 2.89 0 0 0 0 4.134l.637.622-.011.89a2.89 2.89 0 0 0 2.924 2.924l.89-.01.622.636a2.89 2.89 0 0 0 4.134 0l.622-.637.89.011a2.89 2.89 0 0 0 2.924-2.924l-.01-.89.636-.622a2.89 2.89 0 0 0 0-4.134l-.637-.622.011-.89a2.89 2.89 0 0 0-2.924-2.924l-.89.01-.622-.636zM6.854 11.354l-2.5-2.5.708-.708L6.854 9.9l3.792-3.793.708.708-4.5 4.5z" />
                                        </svg>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Verification Request Button */}
                    {!user.verified && (user.is_brand || user.is_content_producer) && (
                        <div className="w-full flex justify-center mb-4">
                            <button
                                onClick={user.verification_request === null ? handleVerificationRequest : undefined}
                                disabled={user.verification_request !== null}
                                className={`btn-secondary px-4 py-2 text-sm font-semibold flex items-center justify-center gap-2 transition-all shadow-sm ${user.verification_request === null ? 'bg-gradient-to-r from-indigo-50 to-indigo-100 hover:from-indigo-100 hover:to-indigo-200 text-indigo-700 border-indigo-200' : 'bg-slate-100 text-slate-500 border-slate-200 opacity-80 cursor-not-allowed'}`}
                            >
                                <div className={`w-5 h-5 rounded-full flex items-center justify-center shadow-sm flex-shrink-0 ${user.is_brand ? 'bg-yellow-500' : 'bg-blue-500'}`}>
                                    {user.is_brand ? (
                                        <svg xmlns="http://www.w3.org/2000/svg" className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                                            <path fillRule="evenodd" d="M2.166 4.999A11.954 11.954 0 0010 1.944 11.954 11.954 0 0017.834 5c.11.65.166 1.32.166 2.001 0 5.225-3.34 9.67-8 11.317C5.34 16.67 2 12.225 2 7c0-.682.057-1.35.166-2.001zm11.541 3.708a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                                        </svg>
                                    ) : (
                                        <svg xmlns="http://www.w3.org/2000/svg" className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 16 16">
                                            <path d="M10.067.87a2.89 2.89 0 0 0-4.134 0l-.622.638-.89-.011a2.89 2.89 0 0 0-2.924 2.924l.01.89-.636.622a2.89 2.89 0 0 0 0 4.134l.637.622-.011.89a2.89 2.89 0 0 0 2.924 2.924l.89-.01.622.636a2.89 2.89 0 0 0 4.134 0l.622-.637.89.011a2.89 2.89 0 0 0 2.924-2.924l-.01-.89.636-.622a2.89 2.89 0 0 0 0-4.134l-.637-.622.011-.89a2.89 2.89 0 0 0-2.924-2.924l-.89.01-.622-.636zM6.854 11.354l-2.5-2.5.708-.708L6.854 9.9l3.792-3.793.708.708-4.5 4.5z" />
                                        </svg>
                                    )}
                                </div>
                                {user.verification_request === null ? t('profile.requestVerification') : t('profile.verificationRequested')}
                            </button>
                        </div>
                    )}


                    {/* Username & Edit */}
                    <div className="flex items-center justify-center gap-4 mb-3">
                        <h2 className="text-2xl font-bold text-slate-800">{user.username}</h2>
                        <EditProfile />
                    </div>

                    {/* Countries & tags, between the name and the bio */}
                    <ProfileFacets
                        countries={user.profile_countrys}
                        tags={user.profile_tags}
                        className="mb-3"
                    />

                    {/* <ChangeVisibility /> */}

                    {/* Bio */}
                    {user.profile_text && (
                        <div className="bg-slate-50 rounded-2xl p-4 mb-4 text-slate-600 text-sm border border-slate-100 text-center">
                            {user.profile_text}
                        </div>
                    )}

                    {/* Add Link + Links */}
                    <div className="flex justify-center flex-wrap gap-3 mb-2">
                        <AddSocialMedia />
                        <AddLink />
                    </div>

                    {/* One evenly spaced stack. The rows carry no margin of
                        their own now, so an empty list contributes nothing
                        instead of leaving a gap behind. */}
                    <div className="w-full flex flex-col gap-2 mt-4">
                        <RenderSocials />
                        <RenderManuelSocials />
                        <RenderLinks />
                    </div>

                    {/* Gallery — the same grid, tiles and viewer the public
                        profile renders, in the same place in the page, so this
                        screen shows what a visitor gets rather than describing
                        it. Media is arranged on /creator; here it is read-only.
                        Shown to anyone who has media, not only to creators:
                        hiding someone's own uploads from their own profile is
                        how they conclude the upload failed. */}
                    {galleryItems.length > 0 ? (
                        <div className="mt-6">
                            <hr className="border-slate-200 mb-4" />
                            <div className="flex items-center gap-2 mb-3">
                                <h3 className="text-sm font-bold text-slate-800">{t('creator.gallery')}</h3>
                                <span className="text-[11px] font-semibold text-slate-400 tabular-nums">
                                    {galleryItems.length}
                                </span>
                                <Link
                                    to="/creator"
                                    className="ml-auto text-[11px] font-semibold text-primary-600 hover:text-primary-700 no-underline"
                                >
                                    {t('creator.addMedia')}
                                </Link>
                            </div>
                            <MediaGallery
                                items={galleryItems}
                                columns="grid-cols-2 sm:grid-cols-3"
                                aspect="portrait"
                                gap="gap-3"
                                renderBadge={(item) => (
                                    <MediaTypeBadge
                                        type={item.type}
                                        className="absolute top-2 left-2 opacity-0 group-hover:opacity-100 transition-opacity duration-200"
                                    />
                                )}
                            />
                        </div>
                    ) : user.is_content_producer && (
                        /* An empty gallery is worth a word only to the accounts
                           whose profile is expected to carry one — and the word
                           worth saying is where to add it. */
                        <div className="mt-6">
                            <hr className="border-slate-200 mb-4" />
                            <Link
                                to="/creator"
                                className="flex flex-col items-center justify-center gap-2 py-8 rounded-2xl
                                    border-2 border-dashed border-slate-200 text-slate-400
                                    hover:border-primary-400 hover:text-primary-600 hover:bg-primary-50/40
                                    transition-colors no-underline"
                            >
                                <svg xmlns="http://www.w3.org/2000/svg" className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 15.75l5.159-5.159a2.25 2.25 0 013.182 0l5.159 5.159m-1.5-1.5l1.409-1.409a2.25 2.25 0 013.182 0l2.909 2.909M3.75 21h16.5A2.25 2.25 0 0022.5 18.75V5.25A2.25 2.25 0 0020.25 3H3.75A2.25 2.25 0 001.5 5.25v13.5A2.25 2.25 0 003.75 21z" />
                                </svg>
                                <span className="text-xs font-semibold text-center px-4">{t('creator.noMedia')}</span>
                            </Link>
                        </div>
                    )}

                    <div className="my-10"></div>
                </div>
            </div>

            {/* The saved theme comes back from the server rather than from the
                draft: it clamps and drops values, so the copy held here has to
                be the one that was actually stored. */}
            <ThemeStudio
                open={themeOpen}
                onClose={() => setThemeOpen(false)}
                theme={user.theme}
                username={user.username}
                avatar={user.profile_picture}
                onSaved={(saved) => setUser((prev) => ({ ...prev, theme: saved }))}
            />
        </div>
    );
}
export default Profile;
